import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Copy, Check, Download } from "lucide-react";
import { useState } from "react";

/**
 * Reusable ShareModal — used both in the main app and as SDK primitive.
 * Props:
 *   open, onOpenChange, shortUrl, title
 */
export function ShareModal({ open, onOpenChange, shortUrl, title = "Share this link" }) {
    const [copied, setCopied] = useState(false);
    if (!shortUrl) return null;

    const copy = async () => {
        await navigator.clipboard.writeText(shortUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const encoded = encodeURIComponent(shortUrl);
    const socials = [
        { name: "WhatsApp", href: `https://wa.me/?text=${encoded}` },
        { name: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encoded}` },
        { name: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encoded}` },
        { name: "Telegram", href: `https://t.me/share/url?url=${encoded}` },
        { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}` },
        { name: "Email", href: `mailto:?body=${encoded}` },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="share-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{title}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-[auto_1fr] items-start gap-6">
                    <div className="rounded-md bg-white p-3">
                        <QRCodeSVG value={shortUrl} size={140} fgColor="#0F172A" bgColor="#ffffff" />
                    </div>
                    <div className="flex flex-col gap-3">
                        <div className="rounded-md border border-border bg-muted/40 p-3">
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Short link</div>
                            <div className="mt-1 break-all font-mono text-sm">{shortUrl}</div>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={copy} size="sm" className="gap-2" data-testid="share-copy-btn">
                                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                                {copied ? "Copied" : "Copy"}
                            </Button>
                            <Button variant="outline" size="sm" asChild data-testid="share-download-btn">
                                <a href={shortUrl.replace(/\/([^/]+)$/, "/api/qr/$1?fmt=png")} download className="gap-2">
                                    <Download className="h-3.5 w-3.5" /> QR PNG
                                </a>
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2">
                    {socials.map((s) => (
                        <a
                            key={s.name}
                            href={s.href}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md border border-border bg-card px-3 py-2 text-center text-sm hover:bg-secondary"
                            data-testid={`share-${s.name.toLowerCase().split(" ")[0]}-btn`}
                        >
                            {s.name}
                        </a>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
